import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'use-intl';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Loader2 } from "lucide-react";
import { SaveSettingsBar } from "./SaveSettingsBar";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { legalFormChangeConfirmation, type LegalFormChangeConfirmation } from "@/utils/errorUtils";

// `company_context.company_type`'s CHECK values, in the order the picker offers them; each has a
// catalog label (`consultantSettings.context.types`).
const COMPANY_TYPES = ['ef', 'ab', 'hb', 'kb', 'ekonomisk_forening', 'annat'] as const;

export const CompanyContextSettings = () => {
  const t = useTranslations('consultantSettings.context');
  const [contextText, setContextText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyType, setCompanyType] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  // FIN-1167 EC3: a legal-form change the backend holds because tax rows are already booked.
  const [heldChange, setHeldChange] = useState<LegalFormChangeConfirmation | null>(null);
  const savingRef = useRef(false);

  const companySettingsService = ServiceRegistry.getCompanySettingsService();

  useEffect(() => {
    loadContext();
  }, []);

  const loadContext = async () => {
    try {
      setLoading(true);
      const context = await companySettingsService.getContext();
      setContextText(context.context_text);
      setCompanyType(context.company_type);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading company context:', error);
      // Error toast already shown by API client
    } finally {
      setLoading(false);
    }
  };

  const save = async (confirmLegalFormChange: boolean) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await companySettingsService.updateContext(contextText, companyType, { confirmLegalFormChange });
      setHasChanges(false);
      setHeldChange(null);
      // Success toast already shown by API client
    } catch (error) {
      const held = legalFormChangeConfirmation(error);
      if (held) {
        setHeldChange(held);
      } else {
        console.error('Error saving company context:', error);
        // Error toast already shown by API client
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleSave = () => save(false);

  const handleTextChange = (value: string) => {
    setContextText(value);
    setHasChanges(true);
  };

  const handleCompanyTypeChange = (value: string) => {
    setCompanyType(value);
    setHasChanges(true);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t('title')}
        </CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="company-type">{t('companyType')}</Label>
          {loading ? (
            <div className="h-10" />
          ) : (
            <Select onValueChange={handleCompanyTypeChange} value={companyType ?? undefined} disabled={saving}>
              <SelectTrigger id="company-type">
                <SelectValue placeholder={t('companyTypePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {COMPANY_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{t(`types.${type}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="company-context">
            {t('textLabel')}
          </Label>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Textarea
              id="company-context"
              placeholder={t('placeholder')}
              value={contextText}
              onChange={(e) => handleTextChange(e.target.value)}
              className="min-h-[200px] font-mono text-sm"
              disabled={saving}
            />
          )}

          <p className="text-xs text-muted-foreground">
            {t('hint')}
          </p>
        </div>

        <SaveSettingsBar onSave={handleSave} status={{ loading, saving, hasChanges }} namespace="consultantSettings.context" />

        <AlertDialog
          open={heldChange !== null}
          onOpenChange={(open) => { if (!open && !savingRef.current) setHeldChange(null); }}
        >
          <AlertDialogContent data-testid="legal-form-change-confirmation">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('legalFormChange.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {heldChange && t('legalFormChange.body', { count: heldChange.approvedTaxPostingCount })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saving}>{t('legalFormChange.cancel')}</AlertDialogCancel>
              <Button onClick={() => save(true)} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('legalFormChange.changing')}
                  </>
                ) : (
                  t('legalFormChange.confirm')
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
